import { useState, useEffect, useMemo} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import userService from '../services/userService';
import { validateUsername } from '../utils/validators';
import { debounce } from 'lodash';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const useUsernameSelection = (onSuccess) => {
  const { getAccessTokenSilently } = useAuth0();
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameError, setUsernameError] = useState(null);
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const checkAvailability = useMemo(
    () => debounce(async (username) => {
      if (!username || username.length < 3) {
        setUsernameAvailable(null);
        return;
      }

      setCheckingAvailability(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/check-username/${username}`);
        const data = await response.json();
        setUsernameAvailable(data.available);
      } catch (err) {
        console.error('Error checking username:', err);
      } finally {
        setCheckingAvailability(false);
      }
    }, 500),
    [] // Empty array since debounce creates a stable function
  );

  useEffect(() => {
    const error = validateUsername(usernameInput);
    setUsernameError(error);

    if (!error && usernameInput) {
      checkAvailability(usernameInput);
    } else {
      setUsernameAvailable(null);
    }
  }, [usernameInput, checkAvailability]);

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsernameInput(value);
    setUsernameAvailable(null);
  };

  const submitUsername = async () => {
    const error = validateUsername(usernameInput);
    if (error) {
      setUsernameError(error);
      return;
    }

    if (usernameAvailable === false) {
      setUsernameError('This username is already taken');
      return;
    }

    setUsernameLoading(true);
    setUsernameError(null);

    try {
      const token = await getAccessTokenSilently();
      const updatedUser = await userService.updateUsername(token, usernameInput);
      onSuccess(updatedUser);
      setUsernameInput('');
    } catch (err) {
      setUsernameError(err.message);
    } finally {
      setUsernameLoading(false);
    }
  };

  return {
    usernameInput,
    usernameError,
    usernameLoading,
    usernameAvailable,
    checkingAvailability,
    handleUsernameChange,
    submitUsername
  };
};